import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  CircularProgress,
  Typography,
  Box,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { TherapyChip } from "../../../../../../atoms/Chip";
import { useDebounce } from "../../../Base/hooks/useDebounce";
import { useSearchRxByPatientName } from "../../../../../../stores/Transactions/insurance-query";
import { RxStatusColors } from "../../../../../../themes/mobiliTheme";
import { formatDateUI } from "../../../../../../helpers/dateFormat";
import { methodColorKey, termColorKey } from "../../../../../../helpers/slotTherapy";

interface SpotlightSearchProps {
  open: boolean;
  onClose: () => void;
  onRxSelect: (rx: any) => void;
}

export default function SpotlightSearch({ open, onClose, onRxSelect }: SpotlightSearchProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const {
    data: patients = [],
    isLoading,
    isFetching,
  } = useSearchRxByPatientName(debouncedSearchTerm, open);

  /**
   * Reset search term when dialog closes
   */
  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const showLoading = isLoading || isFetching;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "in Behandlung":
        return RxStatusColors.BEING_TREATED;
      case "abgeschlossen":
        return RxStatusColors.FINISHED;
      case "abgerechnet":
        return RxStatusColors.BOOKED;
      case "abgebrochen":
        return RxStatusColors.INTERRUPTED;
      case "zur Kontrolle":
        return RxStatusColors.HAS_ISSUES;
      default:
        return "#616161";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
          <TextField
            autoFocus
            placeholder="Patient suchen (Name eingeben)..."
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9E9E9E" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                backgroundColor: "#FAFAFA",
                "&:hover": { backgroundColor: "#F5F5F5" },
              },
            }}
          />
        </Box>

        {showLoading && (
          <Box py={4} display="flex" justifyContent="center">
            <CircularProgress size={22} sx={{ color: "#9E9E9E" }} />
          </Box>
        )}

        {!showLoading && patients.length > 0 && (
          <Box sx={{ maxHeight: 420, overflowY: "auto", px: 1.5, pb: 2 }}>
            {patients.map((patient: any) => (
              <Box key={patient.id} sx={{ mb: 1.5 }}>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#424242", fontSize: "1.2rem" }}
                  >
                    {patient.firstName} {patient.lastName}
                    {patient.dob && (
                      <Typography
                        component="span"
                        sx={{ ml: 1, color: "#757575", fontSize: "0.9rem", fontWeight: 400 }}
                      >
                        {new Date(patient.dob).toLocaleDateString("de-DE")}
                      </Typography>
                    )}
                  </Typography>
                </Box>

                {(patient.rxs || []).map((rx: any) => {
                  const statusColor = getStatusColor(rx.processingStatus);
                  return (
                    <Box
                      key={rx.id}
                      onClick={() => onRxSelect(rx)}
                      sx={{
                        mx: 0.5,
                        mb: 0.75,
                        p: 1.5,
                        borderRadius: 2,
                        border: "1px solid #EEEEEE",
                        backgroundColor: "#FFFFFF",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: "#F5F7FA",
                          borderColor: "#BDBDBD",
                        },
                      }}
                    >
                      {/* Line 1: Rx Number | Tarif | Date | Mandant */}
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography sx={{ fontWeight: 600, color: "#212121", fontSize: "1rem" }}>
                          {rx.rxNumber ?? "N/B"}
                        </Typography>
                        <Typography sx={{ fontWeight: 500, color: "#546E7A", fontSize: "1rem" }}>
                          {rx.tarifGroup?.id ?? "N/B"}
                        </Typography>
                        <Typography
                          sx={{ color: rx.date ? "#424242" : "#E53935", fontSize: "1rem" }}
                        >
                          {rx.date ? formatDateUI(rx.date) : "Kein Datum"}
                        </Typography>
                        <Typography
                          sx={{
                            ml: "auto",
                            color: rx.account ? "#757575" : "#E53935",
                            fontSize: "1rem",
                          }}
                        >
                          {rx.account?.id ?? "Kein Mandant"}
                        </Typography>
                      </Box>

                      {/* Line 2: Status | Home/Praxis | ICD Codes | Therapies */}
                      <Box display="flex" alignItems="center" gap={2} mt={0.5}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "1rem",
                            letterSpacing: "0.04em",
                            color: statusColor,
                          }}
                        >
                          {(rx.processingStatus ?? "N/B").toUpperCase()}
                        </Typography>
                        <Typography sx={{ color: "#757575", fontSize: "1rem" }}>
                          {rx.isHomeVisit === true
                            ? "Haus/Heim"
                            : rx.isHomeVisit === false
                              ? "Praxis"
                              : "-"}
                        </Typography>
                        <Typography
                          sx={{
                            color: rx.icdCodeEntries?.length > 0 ? "#616161" : "#E53935",
                            fontSize: "1rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          {(rx.icdCodeEntries || []).map((code: any) => code.text).join(", ") ||
                            "Keine ICD-Codes"}
                        </Typography>
                        {(rx.therapyRxs || []).length > 0 && (
                          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                            {rx.therapyRxs.map((tRx: any) => (
                              <TherapyChip
                                key={tRx.id}
                                therapyName={tRx.therapy?.abbreviation ?? "N/B"}
                                colorKey={
                                  tRx.therapy?.therapyMethodId
                                    ? methodColorKey(tRx.therapy.therapyMethodId)
                                    : tRx.prescribedTherapy?.id ?? tRx.prescribedTherapyId
                                      ? termColorKey(
                                          tRx.prescribedTherapy?.id ?? tRx.prescribedTherapyId
                                        )
                                      : undefined
                                }
                                amount={tRx.amount}
                                sx={{ fontSize: "0.85em", height: 26 }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}

        {!showLoading && patients.length === 0 && searchTerm && debouncedSearchTerm && (
          <Box py={4} display="flex" justifyContent="center">
            <Typography variant="body2" sx={{ color: "#9E9E9E" }}>
              Keine passenden Patienten gefunden.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
